/* eslint-disable no-console, @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useSyncExternalStore } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDocumentosVencidos, useDocumentosPorVencer } from './useDocumentosVencidos';
import { useCursosVencidos } from './useCursos';
import { useNavegacionDocumentos } from './useNavegacionDocumentos';
import { usePersonalList } from './usePersonal';
import { useProgramacionSemanal } from './useProgramacion';
import { useEstadisticasServicios } from './useServicios';
import { useAuditoriaDashboard, useAuditoriaEstadisticas } from './useAuditoria';
import { useNotificacionesUsuario, useNotificacionesNoLeidasCount } from './useNotificacionesUsuario';
import { useAreaCatalog } from './useAreaCatalog';
import { apiService } from '../services/api';
import { diasHastaVencimiento } from '../utils/fechaVencimiento';
import {
  NotificacionDocumento,
  CreateNotificacionData,
  UpdateNotificacionData
} from '../types';

// Las notificaciones de BD tienen id numérico (BIGSERIAL, ver sistema.notificacion); las
// sintéticas generadas en este hook usan prefijos de string ('doc_vencido_', 'doc_por_vencer_',
// 'personal_update_'). Se usa para decidir si vale la pena llamar al backend al marcar leída.
const esNotificacionServidor = (id: string) => /^\d+$/.test(id);

// Tipos que emite el barrido de vencimientos del backend
// (zc_backend/services/vencimientosService.js). Los mismos documentos también producen
// notificaciones sintéticas acá abajo, así que hay que deduplicar: ver
// docsConNotificacionVencimiento en generarNotificaciones().
const TIPOS_VENCIMIENTO_SERVIDOR = ['documentos.vencido', 'documentos.por_vencer'];

// Tipos exentos del filtro de "personal no activo" de mapNotifRow.
//
// Ese filtro esconde las notificaciones de gente que ya no está activa (whitelist
// activo/disponible/en servicio en src/hooks/usePersonal.ts). Para estos tipos es
// contraproducente: hablan justo del cambio de estado de la persona, así que el aviso "se
// desvinculó a X" se borraba a sí mismo — la persona deja de ser activa en el mismo momento
// en que la notificación se crea, y nadie llegaba a verla nunca.
const TIPOS_EXENTOS_FILTRO_ACTIVO = new Set([
  'personal.desvinculado',
  'personal.eliminado_fisico',
  'personal.rut_cambiado',
  'personal.estado',
]);

// Clave de descarte compartida entre una notificacion de vencimiento del servidor y su
// gemela sintetica: hablan del MISMO documento pero con ids distintos (id numerico de
// sistema.notificacion vs `doc_vencido_<id>`). Sin ella el descarte no sobrevivia: al
// mandar a papelera la de servidor, el backend la saca de la bandeja activa, deja de
// sembrar docsConNotificacionVencimiento y la sintetica del mismo documento renace en el
// render siguiente con otro id — el usuario borraba y la notificacion reaparecia sola.
const claveDescarteDocumento = (n: Pick<NotificacionDocumento, 'tipo' | 'documento_id'>): string | null => {
  const esVencimiento =
    n.tipo === 'documento_vencido' ||
    n.tipo === 'documento_por_vencer' ||
    TIPOS_VENCIMIENTO_SERVIDOR.includes(n.tipo || '');
  if (!esVencimiento || !n.documento_id) return null;
  return `docvenc_${n.documento_id}`;
};

// Set de ids persistido en localStorage y COMPARTIDO por todas las instancias del hook.
//
// Hay tres montadas a la vez (Header, Layout y NotificacionesModal). Antes cada una tenia
// su propio useState inicializado desde localStorage, asi que descartar o marcar leida en
// el modal no llegaba a las otras: la campana del Header seguia contando la notificacion ya
// borrada hasta recargar la pagina, y como las tres escribian la misma clave con su snapshot
// completo, la ultima en guardar borraba lo que habia registrado otra. Con un unico store
// las tres leen el mismo Set y se re-renderizan juntas.
const crearStoreIds = (key: string) => {
  let ids: Set<string> | null = null;
  const listeners = new Set<() => void>();

  // Lectura perezosa: no se toca localStorage al importar el modulo.
  const getSnapshot = (): Set<string> => {
    if (!ids) {
      try {
        const raw = localStorage.getItem(key);
        const arr = raw ? JSON.parse(raw) : [];
        ids = new Set<string>(Array.isArray(arr) ? arr : []);
      } catch (err) {
        console.warn(`Error leyendo ${key} desde localStorage`, err);
        ids = new Set<string>();
      }
    }
    return ids;
  };

  // La referencia del Set solo cambia cuando cambia el contenido — es lo que
  // useSyncExternalStore compara para decidir si re-renderiza.
  const guardar = (next: Set<string>) => {
    ids = next;
    try {
      localStorage.setItem(key, JSON.stringify(Array.from(next.values())));
    } catch (err) {
      console.warn(`Error guardando ${key} en localStorage`, err);
    }
    listeners.forEach(fn => fn());
  };

  return {
    getSnapshot,
    subscribe: (fn: () => void) => {
      listeners.add(fn);
      return () => { listeners.delete(fn); };
    },
    add: (...nuevos: (string | null | undefined)[]) => {
      const actuales = getSnapshot();
      const aAgregar = nuevos.filter((id): id is string => !!id && !actuales.has(id));
      if (aAgregar.length === 0) return;
      const next = new Set(actuales);
      aAgregar.forEach(id => next.add(id));
      guardar(next);
    },
    remove: (...quitar: (string | null | undefined)[]) => {
      const actuales = getSnapshot();
      const aQuitar = quitar.filter((id): id is string => !!id && actuales.has(id));
      if (aQuitar.length === 0) return;
      const next = new Set(actuales);
      aQuitar.forEach(id => next.delete(id));
      guardar(next);
    },
    clear: () => {
      if (getSnapshot().size === 0) return;
      guardar(new Set<string>());
    },
  };
};

// Leidas localmente (incluye las sinteticas, que no existen en el backend).
const storeLeidas = crearStoreIds('notificaciones_local_leidas_v1');
// "Papelera" local: ids descartados por el usuario, de cualquier origen (BD y sinteticas).
// Se guarda en localStorage porque las sinteticas no existen en el backend y no hay donde
// mas persistir el descarte.
const storeDescartadas = crearStoreIds('notificaciones_local_descartadas_v1');

// Hook principal para gestión de notificaciones de documentos
export const useNotificaciones = () => {
  const queryClient = useQueryClient();
  const { navegarADocumentos } = useNavegacionDocumentos();
  const localLeidas = useSyncExternalStore(storeLeidas.subscribe, storeLeidas.getSnapshot);
  const localDescartadas = useSyncExternalStore(storeDescartadas.subscribe, storeDescartadas.getSnapshot);
  const [forceAllUnread, setForceAllUnread] = useState<boolean>(false);

  const addLocalLeida = (id: string) => storeLeidas.add(id);
  const clearLocalLeidas = () => storeLeidas.clear();

  // `claveExtra` es la clave por documento (ver claveDescarteDocumento): se guarda junto al
  // id para que el descarte tape tambien a la gemela del otro origen.
  const addLocalDescartada = (id: string, claveExtra?: string | null) => storeDescartadas.add(id, claveExtra);
  const removeLocalDescartada = (id: string, claveExtra?: string | null) => storeDescartadas.remove(id, claveExtra);

  // Controla si se consulta la papelera al backend (GET /notificaciones?descartada=true) —
  // solo cuando el usuario realmente la abre en el modal, para no duplicar tráfico en cada
  // carga de la campana.
  const [verPapelera, setVerPapelera] = useState<boolean>(false);

  // Local generated notifications from client-side events (e.g., personal updates)
  const [localGeneradas, setLocalGeneradas] = useState<NotificacionDocumento[]>([]);
  const LOCAL_GENERADAS_KEY = 'notificaciones_local_generadas_v1';

  // Escuchar eventos globales de actualizaciones de personal para generar notificaciones prioridad 'baja'
  useEffect(() => {
    const handler = (ev: Event) => {
      try {
        const detail = (ev as CustomEvent).detail || {};
        const rut = detail.rut || detail.id || String(detail.id || '').trim();
        const nombre = detail.nombre || null;
        const id = `personal_update_${rut}_${Date.now()}`;
        const notif = {
          id,
          tipo: 'personal_actualizacion',
          prioridad: 'baja',
          titulo: nombre ? `Actualización: ${nombre}` : 'Actualización de personal',
          mensaje: `Se actualizó información de la persona ${nombre || rut}`,
          personal_id: rut || null,
          personal_nombre: nombre || null,
          documento_id: null,
          documento_nombre: null,
          fecha_vencimiento: null,
          dias_restantes: null,
          leida: false,
          fecha_creacion: new Date().toISOString(),
          accion_requerida: 'Ver detalles'
        } as NotificacionDocumento;
        setLocalGeneradas(prev => {
          const next = [notif, ...prev.filter(p => p.id !== notif.id)];
          try {
            localStorage.setItem(LOCAL_GENERADAS_KEY, JSON.stringify(next));
          } catch (e) {
            // ignore
          }
          return next;
        });
      } catch (err) {
        console.warn('Error creando notificación local por evento personalUpdated', err);
      }
    };

    window.addEventListener('personalUpdated', handler as EventListener);
    return () => window.removeEventListener('personalUpdated', handler as EventListener);
  }, []);

  // Cargar notificaciones locales generadas desde localStorage al iniciar
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOCAL_GENERADAS_KEY);
      if (raw) {
        const arr = JSON.parse(raw || '[]');
        if (Array.isArray(arr)) setLocalGeneradas(arr);
      }
    } catch (err) {
      // ignore
    }
  }, []);
  
  
  // Estado local para notificaciones eliminadas (leídas)
  // Ahora usamos las notificaciones del backend (/api/auditoria/notificaciones)

  // Obtener datos de documentos vencidos y por vencer
  const { data: documentosVencidos, isLoading: isLoadingVencidos } = useDocumentosVencidos();
  const { data: documentosPorVencer, isLoading: isLoadingPorVencer } = useDocumentosPorVencer();
  // Temporalmente deshabilitado - endpoint /api/cursos/vencidos devuelve error 500
  // const { data: cursosVencidos, isLoading: isLoadingCursos } = useCursosVencidos();
  const cursosVencidos: any = null;
  const isLoadingCursos = false;

  // Obtener datos adicionales para notificaciones más completas
  // Solo se usa para resolver el nombre de la persona en cada notificación (ver
  // resolverNombre más abajo), así que se piden únicamente esas columnas: la lista completa
  // pesaba 302 KB con correo, teléfono, dirección y tallas, en cada página de la app.
  const { data: personalData, isLoading: isLoadingPersonal } = usePersonalList(
    1, 1000, '', { fields: 'id,rut,nombres,nombre,apellido,cargo,estado_id' }
  );
  const { data: estadisticasServicios, isLoading: isLoadingServicios } = useEstadisticasServicios();
  
  // Obtener programación de la semana actual para notificaciones de asignaciones
  const fechaInicioSemana = new Date();
  const diaSemana = fechaInicioSemana.getDay();
  const diff = fechaInicioSemana.getDate() - diaSemana + (diaSemana === 0 ? -6 : 1);
  fechaInicioSemana.setDate(diff);
  fechaInicioSemana.setHours(0, 0, 0, 0);
  
  const { programacion: programacionData, isLoading: isLoadingProgramacion } = useProgramacionSemanal(
    6, // Cartera por defecto
    fechaInicioSemana.toISOString().split('T')[0]
  );

  // Obtener datos de auditoría para notificaciones del sistema
  const { data: auditoriaDashboard, isLoading: isLoadingAuditoriaDashboard } = useAuditoriaDashboard({
    limit: 50,
    es_critico: true
  });
  
  // Notificaciones reales por usuario (BD: sistema.notificacion +
  // sistema.notificacion_destinatario, ver routes/notificaciones.js). Reemplaza a
  // useAuditoriaNotificaciones, que leía sistema.notificaciones (casi vacía, sin fila de
  // lectura por usuario) — ver docs de la migración 047.
  const { data: notificacionesUsuario, isLoading: isLoadingNotificacionesUsuario } = useNotificacionesUsuario({ limit: 50 });
  const { data: noLeidasServidor } = useNotificacionesNoLeidasCount();
  // Papelera: mismo endpoint con descartada=true, solo se pide cuando el usuario la abre
  // (ver setVerPapelera / verPapelera más arriba) para no duplicar tráfico en cada carga.
  const { data: notificacionesPapeleraRaw, isLoading: isLoadingPapelera } = useNotificacionesUsuario(
    { limit: 50, descartada: true },
    { enabled: verPapelera }
  );

  const { data: auditoriaEstadisticas, isLoading: isLoadingAuditoriaEstadisticas } = useAuditoriaEstadisticas(30);

  // Catálogo tipo_documento → área responsable (RRHH / Prevención / N/A), el mismo que usa
  // Estado de Documentación. Las notificaciones de servidor ya traen el área resuelta en
  // metadata; esto es para poder pintar el badge también en las sintéticas, que se arman
  // enteras en cliente. `getArea` viene memoizada, no recalcula en cada render.
  const { getArea } = useAreaCatalog();

  // NOTE: Notifications are now sourced from the backend via useNotificacionesUsuario.
  // The client no longer persists read/removed state in localStorage for these.

  // Helper: resolver nombre completo desde personalData si está disponible. A nivel de hook
  // (no dentro de generarNotificaciones) porque también lo usa el mapeo de la papelera.
  const getNombreFromPersonal = (personalIdOrRut?: string | number | null) => {
    try {
      if (!personalIdOrRut || !personalData?.data?.items) return null;
      const items = personalData.data.items || [];
      const idStr = String(personalIdOrRut);
      const found = items.find((p: any) => String(p.id) === idStr || p.rut === idStr || p.rut?.replace(/[.-]/g, '') === idStr.replace(/[.-]/g, ''));
      if (!found) return null;
      const first = ((found as any).nombre || (found as any).nombres || '').trim();
      const last = ((found as any).apellido || (found as any).apellidos || '').trim();
      return `${first} ${last}`.trim() || null;
    } catch (e) {
      return null;
    }
  };

  // Helper: comprobar si un personal está en estado activo/disponible
  const isPersonalActivo = (personalIdOrRut?: string | number | null) => {
    try {
      if (!personalIdOrRut || !personalData?.data?.items) return null;
      const items = personalData.data.items || [];
      const idStr = String(personalIdOrRut);
      const found = items.find((p: any) => String(p.id) === idStr || p.rut === idStr || p.rut?.replace(/[.-]/g, '') === idStr.replace(/[.-]/g, ''));
      if (!found) return null; // unknown
      // adaptPersonalData normaliza a `activo: boolean`
      return Boolean((found as any).activo);
    } catch (e) {
      return null;
    }
  };

  // Mapea una fila cruda del endpoint /notificaciones (routes/notificaciones.js →
  // listarParaUsuario) a nuestra forma interna. Compartido entre la bandeja activa y la
  // papelera — antes vivía inline dentro de generarNotificaciones() y solo se usaba una vez.
  const mapNotifRow = (notif: any): NotificacionDocumento | null => {
    const tipoNotif = notif.tipo || 'manual';
    // Solo los tipos sintéticos (generados en este mismo hook, más abajo) tienen su
    // prioridad recalculada acá; los tipos de servidor (presisi.*, documentos.*,
    // personal.*, programacion.*, manual) ya vienen con la prioridad decidida por quien
    // creó la notificación — no pisarla.
    let prioridadNotif = (notif.prioridad || 'media') as 'alta' | 'media' | 'baja';
    if (tipoNotif === 'documento_vencido') prioridadNotif = 'alta';
    else if (tipoNotif === 'documento_por_vencer') prioridadNotif = 'media';
    else if (tipoNotif.startsWith('personal_')) prioridadNotif = 'baja'; // sintéticos; los de servidor usan 'personal.' con punto

    const rutPersona = notif.metadata?.rut_persona || null;
    const resolvedNombre = getNombreFromPersonal(rutPersona) || null;

    const id = String(notif.id);

    // Si la notificación es de documento/personal ligada a un RUT, excluir personal no
    // activo (ej. "Desvinculado") cuando se pueda resolver desde el cache de personal.
    // Tiene sentido para el papeleo: no vale la pena insistir con los documentos vencidos de
    // alguien que ya no trabaja acá. Los tipos exentos son otra cosa (ver arriba).
    if (rutPersona && !TIPOS_EXENTOS_FILTRO_ACTIVO.has(tipoNotif) &&
        (tipoNotif.startsWith('documento') || tipoNotif.startsWith('personal'))) {
      const estadoActivo = isPersonalActivo(rutPersona);
      if (estadoActivo === false) return null; // encontrado y no activo -> ignorar
    }

    return {
      id,
      tipo: tipoNotif,
      prioridad: prioridadNotif,
      titulo: notif.titulo || 'Notificación del Sistema',
      mensaje: notif.mensaje || 'Sin mensaje',
      personal_id: rutPersona,
      personal_nombre: resolvedNombre,
      documento_id: notif.entidad_tipo === 'documento' || notif.entidad_tipo === 'pre_sisi_documento' ? notif.entidad_id || null : null,
      documento_nombre: notif.metadata?.nombre_documento || notif.metadata?.tipo_documento || null,
      // Los avisos de vencimiento del backend (documentos.vencido / documentos.por_vencer)
      // sí traen estos datos en metadata — antes estaban fijos en null y el panel de detalle
      // no podía mostrar "Vencido hace N días" para ninguna notificación de servidor.
      fecha_vencimiento: notif.metadata?.fecha_vencimiento ?? null,
      dias_restantes: typeof notif.metadata?.dias_restantes === 'number' ? notif.metadata.dias_restantes : null,
      area: notif.metadata?.area ?? null,
      leida: forceAllUnread ? false : (!!notif.leida || localLeidas.has(id)),
      fecha_creacion: notif.created_at || new Date().toISOString(),
      accion_requerida: 'Revisar',
      entidad_tipo: notif.entidad_tipo || null,
      entidad_id: notif.entidad_id || null,
      metadata: notif.metadata || null,
    } as NotificacionDocumento;
  };

  // Map server notifications (notificacionesUsuario) to our internal shape
  const generarNotificaciones = (): NotificacionDocumento[] => {
    const notificaciones: NotificacionDocumento[] = [];

    // Notificaciones reales de BD (routes/notificaciones.js → listarParaUsuario). Forma que
    // realmente devuelve el endpoint: id, tipo, titulo, mensaje, prioridad, origen,
    // entidad_tipo, entidad_id, metadata, created_at, leida, leida_en — no trae usuario_id
    // ni fecha_vencimiento/dias_restantes (eso es propio de los tipos sintéticos de abajo).
    const notifs = notificacionesUsuario?.data?.notificaciones || [];
    if (Array.isArray(notifs)) {
      notifs.forEach((notif: any) => {
        const mapped = mapNotifRow(notif);
        if (mapped) notificaciones.push(mapped);
      });
    }

    // documentosVencidos y documentosPorVencer son dos llamadas a la API distintas que pueden
    // devolver el MISMO documento cerca del límite de días (cada una reclasifica localmente
    // por fecha más abajo). Sin este Set, un doc.id presente en ambas listas generaba dos
    // notificaciones con el mismo id (`doc_vencido_${id}` o `doc_por_vencer_${id}`) — keys
    // duplicadas en el <ul> de React, que dejaban tarjetas "fantasma" en el DOM sin limpiar al
    // cambiar de filtro (el array lógico llegaba a 0 pero React no reconciliaba bien el árbol
    // con claves repetidas). Un mismo documento nunca debe generar más de una notificación.
    const docsConNotificacionVencimiento = new Set<string>();

    // Y desde que el backend emite sus propios avisos de vencimiento (barrido diario,
    // services/vencimientosService.js), el MISMO documento puede llegar por dos vías: la
    // notificación real de BD y la sintética que se arma acá desde /documentos/vencidos.
    // Un admin/rrhh tiene ambas (es destinatario del barrido Y tiene
    // documents.global_expiry.read), así que vería la tarjeta duplicada. Se siembra el Set
    // con los documentos que ya llegaron por servidor: esa versión gana — trae destinatario
    // real, área resuelta y se puede marcar leída en BD.
    if (Array.isArray(notifs)) {
      notifs.forEach((notif: any) => {
        if (TIPOS_VENCIMIENTO_SERVIDOR.includes(notif.tipo) && notif.entidad_tipo === 'documento' && notif.entidad_id) {
          docsConNotificacionVencimiento.add(String(notif.entidad_id));
        }
      });
    }

    // Añadir notificaciones a partir de documentos vencidos
    const vencidosList = documentosVencidos?.data || documentosVencidos || [];
    if (Array.isArray(vencidosList)) {
      vencidosList.forEach((doc: any) => {
        const docIdStr = String(doc.id);
        if (docsConNotificacionVencimiento.has(docIdStr)) return;
        const rut = doc.personal?.rut || doc.rut_persona || '';
        const nombreResolved = doc.personal?.nombre || doc.personal?.nombres || getNombreFromPersonal(rut) || '';
        const docVId = `doc_vencido_${doc.id}`;
        // calcular días restantes en cliente (puede venir distinto del backend)
        const fechaV = doc.fecha_vencimiento || doc.fecha_venc || null;
        // diasHastaVencimiento normaliza a inicio de día (para que el huso horario no corra
        // un día) y devuelve null si la fecha no es usable — un año imposible no genera
        // aviso, porque "vencido hace 630654 días" no le sirve a nadie.
        const diasRestantes: number | null = diasHastaVencimiento(fechaV);
        // Decidir el estado según el cálculo local de días restantes.
        // Si realmente está vencido (diasRestantes < 0) lo tratamos como vencido.
        // Si no, lo tratamos como por vencer para evitar marcar como vencido por errores del backend.
        if (diasRestantes !== null && diasRestantes < 0) {
          // filtrar por estado del personal si está disponible en el cache de personal
          const estadoActivo = isPersonalActivo(rut || String(doc.personal?.id || doc.rut_persona || ''));
          if (estadoActivo === false) return; // encontrado y no activo -> ignorar
          const mensaje = 'Documento vencido';
          docsConNotificacionVencimiento.add(docIdStr);
          notificaciones.push({
            id: docVId,
            tipo: 'documento_vencido',
            prioridad: 'alta',
            // Sin " — nombre": ya va aparte en personal_nombre (metadata de la tarjeta)
            titulo: doc.nombre_documento,
            mensaje,
            personal_id: rut || String(doc.personal?.id || doc.rut_persona || '' ) || null,
            personal_nombre: nombreResolved || null,
            documento_id: String(doc.id),
            documento_nombre: doc.nombre_documento,
            area: getArea(doc.tipo_documento),
            fecha_vencimiento: fechaV || null,
            dias_restantes: diasRestantes ?? null,
            leida: forceAllUnread ? false : (localLeidas.has(docVId) || false),
            fecha_creacion: doc.fecha_subida || new Date().toISOString(),
            accion_requerida: 'Ver documentos'
          });
        } else {
          // tratar como por vencer si el cálculo local indica que aún no venció
          // y está dentro del umbral de 60 días
          const umbralDias = 60;
          if (diasRestantes !== null && diasRestantes <= umbralDias) {
            const estadoActivo = isPersonalActivo(rut || String(doc.personal?.id || doc.rut_persona || ''));
            if (estadoActivo === false) return; // encontrado y no activo -> ignorar
            const mensaje = `Vence en ${diasRestantes} días`;
            docsConNotificacionVencimiento.add(docIdStr);
            notificaciones.push({
              id: `doc_por_vencer_${doc.id}`,
              tipo: 'documento_por_vencer',
              prioridad: 'media',
              titulo: doc.nombre_documento,
              mensaje,
              personal_id: rut || String(doc.personal?.id || doc.rut_persona || '' ) || null,
              personal_nombre: nombreResolved || null,
              documento_id: String(doc.id),
              documento_nombre: doc.nombre_documento,
              fecha_vencimiento: fechaV || null,
              dias_restantes: diasRestantes ?? null,
              leida: forceAllUnread ? false : (localLeidas.has(docVId) || false),
              fecha_creacion: doc.fecha_subida || new Date().toISOString(),
              accion_requerida: 'Ver documentos'
            });
          }
        }
      });
    }

    // Añadir notificaciones a partir de documentos por vencer
    const porVencerList = documentosPorVencer?.data || documentosPorVencer || [];
    if (Array.isArray(porVencerList)) {
      porVencerList.forEach((doc: any) => {
        const docIdStr = String(doc.id);
        if (docsConNotificacionVencimiento.has(docIdStr)) return;
        const rut = doc.personal?.rut || doc.rut_persona || '';
        const nombreResolved = doc.personal?.nombre || doc.personal?.nombres || getNombreFromPersonal(rut) || '';
        const docPVId = `doc_por_vencer_${doc.id}`;
        const fechaV = doc.fecha_vencimiento || doc.fecha_venc || null;
        const diasRestantes: number | null = diasHastaVencimiento(fechaV);

        // Si la fecha ya pasó, tratarlo como vencido (alta)
        if (diasRestantes !== null && diasRestantes < 0) {
          const estadoActivo = isPersonalActivo(rut || String(doc.personal?.id || doc.rut_persona || ''));
          if (estadoActivo === false) return; // encontrado y no activo -> ignorar
          const mensaje = 'Documento vencido';
          docsConNotificacionVencimiento.add(docIdStr);
          notificaciones.push({
            id: `doc_vencido_${doc.id}`,
            tipo: 'documento_vencido',
            prioridad: 'alta',
            titulo: doc.nombre_documento,
            mensaje,
            personal_id: rut || String(doc.personal?.id || doc.rut_persona || '' ) || null,
            personal_nombre: nombreResolved || null,
            documento_id: String(doc.id),
            documento_nombre: doc.nombre_documento,
            area: getArea(doc.tipo_documento),
            fecha_vencimiento: fechaV || null,
            dias_restantes: diasRestantes,
            leida: forceAllUnread ? false : (localLeidas.has(docPVId) || false),
            fecha_creacion: doc.fecha_subida || new Date().toISOString(),
            accion_requerida: 'Ver documentos'
          });
        } else {
          const umbralDias = 60;
          if (diasRestantes !== null && diasRestantes <= umbralDias) {
            const estadoActivo = isPersonalActivo(rut || String(doc.personal?.id || doc.rut_persona || ''));
            if (estadoActivo === false) return; // encontrado y no activo -> ignorar
            const mensaje = `Vence en ${diasRestantes} días`;
            docsConNotificacionVencimiento.add(docIdStr);
            notificaciones.push({
              id: docPVId,
              tipo: 'documento_por_vencer',
              prioridad: 'media',
              titulo: doc.nombre_documento,
              mensaje,
              personal_id: rut || String(doc.personal?.id || doc.rut_persona || '' ) || null,
              personal_nombre: nombreResolved || null,
              documento_id: String(doc.id),
              documento_nombre: doc.nombre_documento,
              fecha_vencimiento: fechaV || null,
              dias_restantes: diasRestantes ?? null,
              leida: forceAllUnread ? false : (localLeidas.has(docPVId) || false),
              fecha_creacion: doc.fecha_subida || new Date().toISOString(),
              accion_requerida: 'Ver documentos'
            });
          }
        }
      });
    }

    // Añadir notificaciones locales generadas desde eventos (ej. actualizaciones personales)
    if (Array.isArray(localGeneradas) && localGeneradas.length > 0) {
      localGeneradas.forEach((ln) => {
        // Avoid duplicates by id
        if (!notificaciones.find(n => n.id === ln.id)) {
          notificaciones.push({ ...ln });
        }
      });
    }

    return notificaciones;
  };

  // Obtener notificaciones generadas - recalcular cuando cambien las notificaciones del backend.
  // Sin filtrar por papelera todavía — se necesita la versión completa para poder derivar
  // también qué sintéticas/locales están descartadas (ver notificacionesPapelera más abajo).
  const notificacionesGeneradasRaw = React.useMemo(
    () => generarNotificaciones(),
    [generarNotificaciones, notificacionesUsuario?.data, documentosVencidos, documentosPorVencer, personalData, localLeidas, forceAllUnread, localGeneradas, getArea]
  );

  // La clave por documento solo silencia a las sinteticas. A las de servidor las gobierna
  // el flag `descartada` del backend: ocultarlas tambien por clave las dejaria fuera de la
  // papelera (que se pide con descartada=true) y quedarian invisibles e irrecuperables.
  const estaDescartada = React.useCallback((n: NotificacionDocumento): boolean => {
    if (localDescartadas.has(n.id)) return true;
    if (esNotificacionServidor(n.id)) return false;
    const clave = claveDescarteDocumento(n);
    return !!clave && localDescartadas.has(clave);
  }, [localDescartadas]);

  const notificacionesTodas = React.useMemo(
    () => notificacionesGeneradasRaw.filter(n => !estaDescartada(n)),
    [notificacionesGeneradasRaw, estaDescartada]
  );

  // Papelera: notificaciones de servidor descartadas (fetch aparte con descartada=true, ver
  // verPapelera) + sintéticas/locales descartadas (nunca llegan al backend, así que su
  // versión "papelera" sale del mismo array crudo filtrando por localDescartadas).
  const notificacionesPapeleraServidor = React.useMemo(() => {
    const rows = notificacionesPapeleraRaw?.data?.notificaciones || [];
    if (!Array.isArray(rows)) return [];
    return rows.map(mapNotifRow).filter((n): n is NotificacionDocumento => n !== null);
  }, [notificacionesPapeleraRaw?.data, personalData, localLeidas, forceAllUnread]);

  const notificacionesPapeleraLocal = React.useMemo(
    () => notificacionesGeneradasRaw.filter(n => localDescartadas.has(n.id) && !esNotificacionServidor(n.id)),
    [notificacionesGeneradasRaw, localDescartadas]
  );

  const notificacionesPapelera = React.useMemo(() => {
    return [...notificacionesPapeleraServidor, ...notificacionesPapeleraLocal].sort(
      (a, b) => new Date(b.fecha_creacion).getTime() - new Date(a.fecha_creacion).getTime()
    );
  }, [notificacionesPapeleraServidor, notificacionesPapeleraLocal]);

  // Filtrar notificaciones visibles: si están marcadas como leídas (en servidor o local) las ocultamos
  const notificaciones = React.useMemo(() => {
    if (forceAllUnread) return notificacionesTodas;
    return notificacionesTodas.filter(n => !n.leida && !localLeidas.has(n.id));
  }, [notificacionesTodas, localLeidas, forceAllUnread]);

  // El contador visible (campana + encabezado del modal) tiene que ser SIEMPRE el tamaño de
  // la lista que el modal puede mostrar — nunca un número aparte. Antes se sumaba acá el
  // total_no_leidas del servidor (sin tope, sin filtrar por personal activo), mientras el
  // modal solo puede listar `notificaciones` (topada por `limit` en useNotificacionesUsuario
  // y con personal no activo excluido más arriba). Eso producía el bug reportado: la campana
  // decía "12 no leídas" y el modal mostraba "Sin resultados" — dos fuentes de verdad
  // distintas para el mismo número. Con esto, contradicción estructuralmente imposible.
  const notificacionesNoLeidas = notificaciones.length;

  // Cuántas no leídas hay en el servidor que no están en la lista visible (por el tope de
  // `limit` o por quedar excluidas al resolverse a personal no activo). No alimenta el
  // contador principal; solo permite avisar "+K más antiguas" en el footer del modal en vez
  // de dejar que el usuario piense que ya no queda nada pendiente.
  const noLeidasServidorEnLista = notificaciones.filter(n => esNotificacionServidor(n.id)).length;
  const noLeidasOcultas = Math.max(0, (noLeidasServidor?.data?.total_no_leidas || 0) - noLeidasServidorEnLista);

  // Notificaciones leídas (server-marked OR localLeidas)
  const notificacionesLeidas = React.useMemo(() => {
    return notificacionesTodas.filter(n => (n.leida || localLeidas.has(n.id)) && !forceAllUnread);
  }, [notificacionesTodas, localLeidas, forceAllUnread]);
  
  // Agrupar notificaciones por prioridad
  const notificacionesPorPrioridad = {
    alta: notificaciones.filter(n => n.prioridad === 'alta'),
    media: notificaciones.filter(n => n.prioridad === 'media'),
    baja: notificaciones.filter(n => n.prioridad === 'baja')
  };

  // Mutation para marcar una notificación como leída en el backend. Usa apiService (no
  // fetch crudo) para que pasen por el interceptor de axios: adjunta el token del scope
  // activo (plataforma o Pre-SISI, ver src/services/session.ts) y el baseURL correcto —
  // un fetch('/api/...') relativo no llevaría Authorization ni resolvería el host en
  // producción, donde front y backend no comparten origen.
  const marcarComoLeida = useMutation({
    mutationFn: async (notificacionId: string) => {
      return apiService.marcarNotificacionLeida(notificacionId);
    },
    onSuccess: (_, notificacionId) => {
      // Invalidar la query de notificaciones para refrescar el estado
      queryClient.invalidateQueries(['notificaciones', 'usuario']);
      console.log(`Notificación ${notificacionId} marcada como leída en servidor`);
    },
  });

  // Helper optimista: marca localmente primero para que la UI cambie inmediatamente,
  // luego intenta marcar en backend cuando aplique. Para notificaciones locales
  // (synthetic) solo se marca localmente.
  const marcarComoLeidaOptimistic = async (notificacionId: string) => {
    try {
      addLocalLeida(notificacionId);
      queryClient.invalidateQueries(['notificaciones', 'usuario']);
    } catch (e) {
      // ignore
    }

    // Client-only notifications: skip server call
    if (!esNotificacionServidor(notificacionId)) {
      return;
    }

    try {
      await marcarComoLeida.mutateAsync(notificacionId);
    } catch (err) {
      // If server call fails, keep the local mark (best-effort)
      try { addLocalLeida(notificacionId); } catch (_) {}
      queryClient.invalidateQueries(['notificaciones', 'usuario']);
    }
  };

  // Mutation para marcar todas las notificaciones como leídas — un solo request al
  // backend (PUT /api/notificaciones/marcar-todas-leidas), ya no una iteración por id.
  const marcarTodasComoLeidas = useMutation({
    mutationFn: async () => {
      return apiService.marcarTodasNotificacionesLeidas();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notificaciones', 'usuario']);
      console.log('Todas las notificaciones marcadas como leídas en servidor');
    },
  });

  const marcarTodasComoLeidasOptimistic = async () => {
    // Optimistic: marcar localmente todas primero para respuesta inmediata (cubre también
    // las sintéticas, que nunca pasan por el servidor).
    const ids = notificaciones.map(n => n.id);
    for (const id of ids) {
      try { addLocalLeida(id); } catch (e) { /* ignore */ }
    }
    queryClient.invalidateQueries(['notificaciones', 'usuario']);

    // Un solo request para todas las de servidor (PUT /marcar-todas-leidas) en vez de un
    // request por id: antes cada marcarComoLeida.mutateAsync individual invalidaba la query
    // pero no el conteo de no-leidas-count, dejando hasta 60s (refetchInterval) de desfase
    // entre "lista vacía" y "campana con no leídas" tras pulsar el botón.
    if (ids.some(esNotificacionServidor)) {
      try {
        await marcarTodasComoLeidas.mutateAsync();
      } catch (err) {
        // best-effort: ya quedaron marcadas localmente arriba
      }
    }
  };

  // Mutation para enviar una notificación a la papelera (DELETE /api/notificaciones/:id —
  // borra solo la copia de este usuario, ver services/notificacionesService.js).
  const eliminarNotificacion = useMutation({
    mutationFn: async (notificacionId: string) => {
      return apiService.eliminarNotificacion(notificacionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notificaciones', 'usuario']);
    },
  });

  // Igual patrón que marcarComoLeidaOptimistic: se descarta localmente de inmediato (cubre
  // también las sintéticas, que no existen en el backend) y solo se llama al servidor si el
  // id es de una notificación real.
  const descartarNotificacionOptimistic = async (notificacionId: string) => {
    const notif = notificacionesGeneradasRaw.find(n => n.id === notificacionId);
    addLocalDescartada(notificacionId, notif ? claveDescarteDocumento(notif) : null);

    if (!esNotificacionServidor(notificacionId)) {
      return;
    }

    try {
      await eliminarNotificacion.mutateAsync(notificacionId);
    } catch (err) {
      // best-effort: ya quedó descartada localmente
    }
  };

  // Mutation para restaurar una notificación desde la papelera (PUT /:id/restaurar).
  const restaurarNotificacion = useMutation({
    mutationFn: async (notificacionId: string) => {
      return apiService.restaurarNotificacion(notificacionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notificaciones', 'usuario']);
    },
  });

  const restaurarNotificacionOptimistic = async (notificacionId: string) => {
    // La notificacion restaurada puede estar en la papelera del servidor (ya no aparece en
    // el array crudo de la bandeja), asi que se busca en ambas listas para recuperar su
    // clave por documento y liberar tambien a la gemela sintetica.
    const notif =
      notificacionesGeneradasRaw.find(n => n.id === notificacionId) ||
      notificacionesPapelera.find(n => n.id === notificacionId);
    removeLocalDescartada(notificacionId, notif ? claveDescarteDocumento(notif) : null);

    if (!esNotificacionServidor(notificacionId)) {
      return;
    }

    try {
      await restaurarNotificacion.mutateAsync(notificacionId);
    } catch (err) {
      // best-effort: ya quedó restaurada localmente
    }
  };

  // Función para obtener el color de la prioridad
  const getColorPrioridad = (prioridad: 'alta' | 'media' | 'baja') => {
    switch (prioridad) {
      case 'alta':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'media':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'baja':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Función para obtener el icono del tipo de notificación
  const getIconoTipo = (tipo: string) => {
    switch (tipo) {
      case 'documento_vencido':
        return '⚠️';
      case 'documento_por_vencer':
        return '⏰';
      case 'documento_faltante':
        return '📋';
      case 'documento_renovado':
        return '✅';
      case 'personal_sin_asignacion':
        return '👤';
      case 'servicios_sin_personal':
        return '🏢';
      case 'programacion_pendiente':
        return '📅';
      case 'mantenimiento_proximo':
        return '🔧';
      case 'auditoria_critica':
        return '🔴';
      case 'auditoria_sistema':
        return '🔔';
      case 'auditoria_estadisticas':
        return '📊';
      // Tipos reales de BD (routes/pre-sisi.js y los enganches del SISI en Fase 2)
      case 'presisi.pendiente':
        return '📥';
      case 'presisi.aprobado':
        return '✅';
      case 'presisi.rechazado':
        return '❌';
      case 'manual':
        return '📢';
      default:
        if (tipo.startsWith('documentos.')) return '📄';
        if (tipo.startsWith('personal.')) return '👤';
        if (tipo.startsWith('programacion.')) return '📅';
        return '📄';
    }
  };

  // Función para formatear fecha
  const formatearFecha = (fecha: string) => {
    return new Date(fecha).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return {
    // Datos
    notificaciones,
    notificacionesTodas,
    notificacionesLeidas,
    notificacionesNoLeidas,
    notificacionesPapelera,
    noLeidasOcultas,
    notificacionesPorPrioridad,

    // Estados de carga
    isLoading: isLoadingVencidos || isLoadingPorVencer || isLoadingCursos || isLoadingPersonal || isLoadingServicios || isLoadingProgramacion || isLoadingAuditoriaDashboard || isLoadingNotificacionesUsuario || isLoadingAuditoriaEstadisticas,
    isLoadingPapelera,

    // Mutations
    marcarComoLeida,
    marcarTodasComoLeidas,
    marcarComoLeidaOptimistic,
    marcarTodasComoLeidasOptimistic,
    eliminarNotificacion,
    descartarNotificacionOptimistic,
    restaurarNotificacion,
    restaurarNotificacionOptimistic,
    // Local read set (used by UI to reflect optimistic reads immediately)
    localLeidas,
    // Activa/desactiva el fetch de la papelera al backend (ver verPapelera más arriba)
    setVerPapelera,

    // Funciones de utilidad
    getColorPrioridad,
    getIconoTipo,
    formatearFecha,
    navegarADocumentos,

    // Estados de mutations
    isMarkingAsRead: marcarComoLeida.isLoading,
    isMarkingAllAsRead: marcarTodasComoLeidas.isLoading,
    isDismissing: eliminarNotificacion.isLoading,
    isRestoring: restaurarNotificacion.isLoading,
  };
};
